import { useEffect } from 'react';
import * as Sentry from '@sentry/react';
import type { AxiosError } from 'axios';
import { matchRoutes, useLocation, useNavigationType, createRoutesFromChildren } from 'react-router-dom';

import { scrubEvent, scrubBreadcrumb, scrubReplayFrame } from './sentry-scrub';

Sentry.init({
  dsn: 'https://7eb0e4dfba65f849c7bafc3197f29010@o1181643.ingest.us.sentry.io/4506937804455936',
  environment: import.meta.env.MODE,
  // SDK default is already false; kept explicit so an upgrade can't change it silently.
  sendDefaultPii: false,
  integrations: [
    // Router-aware tracing: transactions are named by route pattern
    // (`/dashboard/details/:patientId`), never by the concrete URL.
    Sentry.reactRouterBrowserTracingIntegration({
      useEffect,
      useLocation,
      useNavigationType,
      createRoutesFromChildren,
      matchRoutes,
    }),
    Sentry.replayIntegration({
      // SDK defaults, kept explicit: the clinician UI is PHI.
      maskAllText: true,
      maskAllInputs: true,
      blockAllMedia: true,
      beforeAddRecordingEvent: scrubReplayFrame,
    }),
  ],
  // Performance Monitoring
  tracesSampleRate: import.meta.env.PROD ? 0.2 : 1.0,
  // Set 'tracePropagationTargets' to control for which URLs distributed tracing should be enabled
  tracePropagationTargets: ['localhost'],
  // Session Replay: no routine recording of the clinician UI, only sessions in which an error occurred.
  replaysSessionSampleRate: 0,
  replaysOnErrorSampleRate: 1.0,
  beforeBreadcrumb: scrubBreadcrumb,
  beforeSend(event, hint) {
    const error = hint?.originalException as AxiosError | undefined;

    if (error?.isAxiosError) {
      const status = error.response?.status;

      // Ignore 401 and 404
      if (status === 401 || status === 404) {
        return null; // prevents sending to Sentry
      }
    }

    return scrubEvent(event);
  },
  beforeSendTransaction: (event) => scrubEvent(event),
});
